import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { X, QrCode, Plus, Trash2, Loader2, Sparkles, CreditCard, CheckCircle } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

const ManualTicketModal = ({ event, onClose }) => {
    const { slug } = useParams();
    const [statusData, setStatusData] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [guests, setGuests] = useState([{ name: '', email: '' }]);
    const [selectedTicketType, setSelectedTicketType] = useState('');
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [successCount, setSuccessCount] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await axios.get(`/${slug}/api/subscriptions/status`, { withCredentials: true });
                setStatusData(res.data);
                if (event.ticketTypes && event.ticketTypes.length > 0) {
                     setSelectedTicketType(event.ticketTypes[0]._id);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingStatus(false);
            }
        };
        fetchStatus();
    }, [slug, event]);

    const handleAddGuest = () => {
        setGuests([...guests, { name: '', email: '' }]);
    };

    const handleRemoveGuest = (index) => {
        setGuests(guests.filter((_, i) => i !== index));
    };

    const handleChange = (index, field, value) => {
        const newGuests = [...guests];
        newGuests[index][field] = value;
        setGuests(newGuests);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validGuests = guests.filter(g => g.name.trim() && g.email.trim());
        if (validGuests.length === 0) {
            setErrorMsg('Por favor ingresa al menos un invitado con correo válido.');
            return;
        }

        setLoadingSubmit(true);
        setErrorMsg('');
        
        try {
            const res = await axios.post(`/${slug}/api/tickets/manual-issue`, {
                eventId: event._id,
                guests: validGuests,
                ticketTypeId: selectedTicketType || null
            }, { withCredentials: true });
            
            setSuccessCount(res.data.count);
        } catch (err) {
            setErrorMsg(err.response?.data?.error || 'Error al generar accesos manuales.');
        } finally {
            setLoadingSubmit(false);
        }
    };

    if (loadingStatus) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md">
                 <Loader2 className="animate-spin text-white" size={32} />
            </div>
        );
    }

    const isBookingEvent = event.salesMode === 'booking';
    const isSubscribed = isBookingEvent || (statusData?.billingModel === 'subscription' && statusData?.subscriptionStatus === 'active');
    const isPastDue = statusData?.billingModel === 'subscription' && statusData?.subscriptionStatus === 'past_due';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 px-2 sm:px-4">
            <div className="bg-[#080808] border border-white/10 rounded-[32px] w-full max-w-2xl max-h-[90vh] overflow-y-auto relative shadow-2xl pb-10">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 bg-white/5 rounded-full hover:bg-white/10 transition-all z-10"
                >
                    <X size={20} className="text-white" />
                </button>

                <div className="p-6 md:p-8">
                    <div className="mb-6">
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#009EE3] mb-2 flex items-center gap-1">
                            <QrCode size={12} /> Emisión Manual (Efectivo/VIP)
                        </p>
                        <h2 className="text-2xl font-black text-white">{event.title}</h2>
                    </div>

                    {!isSubscribed ? (
                        <div className="bg-white/5 border-2 border-dashed border-white/10 rounded-3xl p-8 text-center mt-8">
                            {isPastDue ? (
                                <>
                                    <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Loader2 className="text-red-500" size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Pago Rechazado</h3>
                                    <p className="text-white/50 text-sm mb-6 max-w-sm mx-auto">
                                        Tu suscripción mensual rebotó o te quedaste sin límite. Esta herramienta se encuentra bloqueada por falta de pago.
                                    </p>
                                    <Link to={`/${slug}/admin/billing`} className="px-6 py-3 bg-red-500 text-white font-bold rounded-xl inline-flex">
                                        Regularizar pago
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 bg-[#009EE3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Sparkles className="text-[#009EE3]" size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">Suscripción SaaS Inactiva</h3>
                                    <p className="text-white/50 text-sm mb-6 max-w-md mx-auto">
                                        Esta es una función exclusiva para Salones suscritos bajo nuestro modelo de pago mensual sin comisiones. Desbloquea la generación de QRs ilimitados en efectivo.
                                    </p>
                                    <Link to={`/${slug}/admin/billing`} className="px-6 py-3 bg-[#009EE3] hover:bg-[#0089C7] text-white font-bold rounded-xl shadow-lg inline-flex items-center gap-2">
                                        <CreditCard size={18} /> Conocer más
                                    </Link>
                                </>
                            )}
                        </div>
                    ) : successCount > 0 ? (
                         <div className="bg-green-500/10 border border-green-500/20 rounded-3xl p-8 text-center mt-8 flex flex-col items-center">
                              <CheckCircle className="text-green-500 mb-4" size={48} />
                              <h3 className="text-2xl font-black text-white mb-2">¡{successCount} Entradas Emitidas!</h3>
                              <p className="text-white/60 mb-8 max-w-sm">Los QRs han sido generados exitosamente y enviados por correo a cada invitado. Ya figuran en la lista de accesos del evento.</p>
                              
                              <div className="flex gap-4">
                                   <button onClick={() => { setSuccessCount(0); setGuests([{name:'', email:''}]); }} className="px-6 py-3 bg-white/10 hover:bg-white/20 font-bold text-white rounded-xl">Emitir Más</button>
                                   <button onClick={onClose} className="px-6 py-3 bg-primary-orange font-bold text-white rounded-xl">Cerrar</button>
                              </div>
                         </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="mt-8">
                             {errorMsg && (
                                 <div className="bg-red-500/10 text-red-400 border border-red-500/30 rounded-xl p-4 mb-6 text-sm">
                                      {errorMsg}
                                 </div>
                             )}

                             {event.ticketTypes?.length > 0 && (
                                 <div className="mb-6">
                                     <label className="block text-xs font-bold text-white/40 uppercase mb-2">Tipo de Entrada</label>
                                     <select 
                                         value={selectedTicketType} 
                                         onChange={e => setSelectedTicketType(e.target.value)}
                                         className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary-orange"
                                     >
                                         <option value="">Entrada General (Default)</option>
                                         {event.ticketTypes.map(t => (
                                             <option key={t._id} value={t._id}>{t.name} (Aforo: {t.totalQuantity})</option>
                                         ))}
                                     </select>
                                 </div>
                             )}

                            <div className="space-y-4">
                                {guests.map((g, index) => (
                                    <div key={index} className="flex flex-col sm:flex-row gap-3 items-start sm:items-center bg-white/5 p-4 rounded-xl border border-white/5 relative">
                                        <div className="flex-1 w-full">
                                            <label className="block text-[10px] font-bold text-white/40 uppercase mb-1">Nombre Completo</label>
                                            <input 
                                                type="text" 
                                                placeholder="Ej. Juan Perez"
                                                value={g.name}
                                                onChange={e => handleChange(index, 'name', e.target.value)}
                                                className="w-full bg-transparent border-b border-white/10 pb-1 text-white text-sm focus:outline-none focus:border-primary-orange"
                                                required
                                            />
                                        </div>
                                        <div className="flex-1 w-full">
                                            <label className="block text-[10px] font-bold text-white/40 uppercase mb-1">Correo Electrónico</label>
                                            <input 
                                                type="email" 
                                                placeholder="juan@gmail.com"
                                                value={g.email}
                                                onChange={e => handleChange(index, 'email', e.target.value)}
                                                className="w-full bg-transparent border-b border-white/10 pb-1 text-white text-sm focus:outline-none focus:border-primary-orange"
                                                required
                                            />
                                        </div>
                                        {guests.length > 1 && (
                                            <button 
                                                type="button" 
                                                onClick={() => handleRemoveGuest(index)} 
                                                className="absolute top-2 right-2 sm:static sm:mt-4 p-2 text-white/30 hover:text-red-500 hover:bg-white/5 rounded-lg"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <button 
                                type="button" 
                                onClick={handleAddGuest}
                                className="mt-4 flex items-center gap-2 text-xs font-bold text-[#009EE3] hover:text-white px-4 py-2 bg-[#009EE3]/10 rounded-xl transition-colors"
                            >
                                <Plus size={14} /> Añadir otro invitado
                            </button>

                            <div className="mt-8 pt-6 border-t border-white/10 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 rounded-xl font-bold text-white/60 hover:text-white hover:bg-white/5 transition-all text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loadingSubmit}
                                    className="px-8 py-3 rounded-xl font-black text-white bg-brand-gradient shadow-[0_0_15px_-5px_orange] hover:scale-105 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
                                >
                                    {loadingSubmit ? <Loader2 className="animate-spin" size={18} /> : <QrCode size={18} />}
                                    GENERAR {guests.filter(g => g.name).length} QRS
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualTicketModal;
